/**
 * Portal detection — subdomain-based login routing.
 *
 * One SPA serves three personas; the hostname's FIRST label decides which
 * portal a domain "belongs" to:
 *
 *   vendor.swiftlane.com → vendor portal   (login /vendor/login, app /vendor/*)
 *   ops.swiftlane.com    → command center  (login /ops/login,    app /admin/*)
 *   anything else        → PM portal       (login /login — current behavior,
 *                                           pm.swiftlane.com / staging / localhost)
 *
 * IMPORTANT — route by subdomain, don't LOCK by subdomain. Every path keeps
 * working on every host (emailed magic links and deep links must never break).
 * The portal only controls DEFAULTS: which login page `/login` resolves to and
 * where unauthenticated users are redirected. RBAC + token audience remain the
 * only security boundary — the ops subdomain is UX, not isolation.
 *
 * Local testing without DNS/hosts changes: Chrome resolves `vendor.localhost`
 * and `ops.localhost` to 127.0.0.1 automatically, so
 * `http://vendor.localhost:5173` exercises the vendor portal end to end.
 */

import i18n from '../i18n';

export type Portal = 'pm' | 'ops' | 'vendor';

/** Detect the portal from the hostname's first label. */
export function detectPortal(hostname?: string): Portal {
  const host = (
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')
  ).toLowerCase();
  const first = host.split('.')[0];
  if (first === 'vendor' || first === 'vendors') return 'vendor';
  if (first === 'ops' || first === 'admin-ops') return 'ops';
  return 'pm';
}

/** The login page this host's users should land on. */
export function portalLoginPath(portal: Portal = detectPortal()): string {
  switch (portal) {
    case 'vendor':
      return '/vendor/login';
    case 'ops':
      return '/ops/login';
    default:
      return '/login';
  }
}

/**
 * Browser-tab title per portal, applied once at app boot. Returns the title
 * it set (or null when the default index.html title is kept), so callers and
 * tests can assert without reaching into the DOM.
 */
export function applyPortalDocumentTitle(portal: Portal = detectPortal()): string | null {
  // i18n (spec 91 R3): resolved via the i18n SINGLETON (called at app boot,
  // outside React render). `defaultValue` is byte-identical to the historical
  // English so `en` is unchanged. "SwiftOps" is the product name (untranslated).
  const title =
    portal === 'vendor'
      ? i18n.t('common:portalTitle.vendor', { defaultValue: 'SwiftOps Vendor Portal' })
      : portal === 'ops'
        ? i18n.t('common:portalTitle.ops', { defaultValue: 'SwiftOps Command Center' })
        : null;
  if (title && typeof document !== 'undefined') document.title = title;
  return title;
}
